import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import { updateTask, deleteTask } from '../api';
import { Button } from './catalyst/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from './catalyst/dialog';
import { Field, FieldGroup, Label } from './catalyst/fieldset';
import { Input } from './catalyst/input';
import { Textarea } from './catalyst/textarea';

export default function TaskModal({ task, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    notes: ''
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        notes: task.notes || ''
      });
    }
  }, [task]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const taskData = {
        id: task.id,
        title: formData.title,
        status: task.status
      };

      // Only add notes if it's not empty
      if (formData.notes) {
        taskData.notes = formData.notes;
      }

      await updateTask(task.taskListId, task.id, taskData);
      onSave();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task');
    }
  };

  const handleDelete = async () => {
    try {
      // Close modal first for smooth UX
      onClose();

      await deleteTask(task.taskListId, task.id);

      // Refresh tasks after deletion
      onSave();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  if (!task) return null;

  return (
    <Dialog size="lg" onClose={onClose}>
      <div className="flex items-start justify-between gap-4">
        <DialogTitle>Edit Task</DialogTitle>
        <Button plain onClick={onClose} className="h-9 w-9 rounded-full p-0" aria-label="Close task editor">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <FieldGroup>
            <Field>
              <Label>Task Name</Label>
              <Input
                type="text"
                placeholder="Task name"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="text-base font-medium"
                required
              />
            </Field>

            <Field>
              <Label>Details</Label>
              <Textarea
                placeholder="Add details"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="6"
                resizable={false}
              />
            </Field>
          </FieldGroup>
        </DialogBody>

        <DialogActions className="justify-between sm:justify-between">
          <Button
            type="button"
            outline
            color="red"
            onClick={handleDelete}
            className="mr-auto text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <div className="flex gap-3">
            <Button type="button" outline onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Save
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}
